import { scopeThreadRef } from "@cmmd-forge/client-runtime";
import type {
  BrowserCommentTarget,
  BrowserCommand,
  BrowserRenderMode,
  BrowserSessionSnapshot,
  BrowserTabSnapshot,
  BrowserViewport,
  ClientOrchestrationCommand,
  ThreadId,
} from "@cmmd-forge/contracts";
import {
  buildBrowserScreenshotAttachmentMetadata,
  hasUsableBrowserScreenshotDataUrl,
  isFreshBrowserCapture,
} from "./BrowserPreviewPanel.logic";
import {
  resolveBrowserAnnotationToggleAction,
  resolveBrowserCommentSubmitAction,
} from "./BrowserPreviewPanel.logic";
import { useCallback, useEffect, useRef } from "react";
import { selectBrowserThreadState, useBrowserStateStore } from "~/browserStateStore";
import type { ComposerImageAttachment } from "~/composerDraftStore";
import { newCommandId, newMessageId, randomUUID } from "~/lib/utils";
import type { Thread } from "~/types";
import { toastManager } from "./ui/toast";
import type { BrowserCommentDraft } from "./browser/browserCommentDraftStore";
import type { BrowserCommandRunOptions } from "./BrowserPreviewPanel.runCommand";
import { buildBrowserCommentPrompt } from "./BrowserPreviewPanel.logic";

const INTERACTIVE_FRAME_FALLBACK_CAPTURE_MS = 2_500;
const VISIBLE_BROWSER_CAPTURE_RETRY_DELAYS_MS = [0, 1_500, 3_000, 6_000] as const;

type BrowserCommentThread = Pick<Thread, "modelSelection" | "runtimeMode" | "interactionMode">;
type BrowserCommentTurnCommand = Extract<ClientOrchestrationCommand, { type: "thread.turn.start" }>;
type BrowserCommentSubmitInput = Parameters<typeof resolveBrowserCommentSubmitAction>[0];
type BrowserAutoCaptureRetryDelays = readonly [number, ...number[]];

interface BrowserAutoCaptureTarget {
  readonly tabId: string;
  readonly url: string;
  readonly updatedAt: string;
}

interface BrowserCommentCaptureOptions {
  readonly activeFrameIsFresh: boolean;
  readonly activeRenderMode: BrowserRenderMode | null;
  readonly activeScreenshotIsFresh: boolean;
  readonly activeScreenshotMeta:
    | {
        readonly url: string;
        readonly capturedAt: string;
        readonly viewport: BrowserViewport;
      }
    | null
    | undefined;
  readonly activeTab: BrowserTabSnapshot | null;
  readonly activeTabUrl: string | null;
  readonly addComposerDraftImage: (
    threadRef: ReturnType<typeof scopeThreadRef>,
    image: ComposerImageAttachment,
  ) => void;
  readonly clearCommentDraft: (threadRef: ReturnType<typeof scopeThreadRef>) => void;
  readonly commentDraft: BrowserCommentDraft;
  readonly canAnnotate: boolean;
  readonly commentOrchestration:
    | {
        readonly dispatchCommand: (command: BrowserCommentTurnCommand) => Promise<unknown>;
      }
    | undefined;
  readonly hasServerThread: boolean;
  readonly runCommand: (
    command: BrowserCommand,
    options?: BrowserCommandRunOptions,
  ) => Promise<boolean>;
  readonly serverThread: BrowserCommentThread | null | undefined;
  readonly session: BrowserSessionSnapshot | null;
  readonly setCommentSelecting: (
    threadRef: ReturnType<typeof scopeThreadRef>,
    selecting: boolean,
  ) => void;
  readonly setCommentTarget: (
    threadRef: ReturnType<typeof scopeThreadRef>,
    target: BrowserCommentTarget | null,
  ) => void;
  readonly shouldPreferBrowserCapture: boolean;
  readonly threadId: ThreadId;
  readonly threadRef: ReturnType<typeof scopeThreadRef>;
}

function createBrowserCommentSubmitInput(input: {
  readonly hasServerThread: boolean;
  readonly target: BrowserCommentTarget | null;
  readonly note: string;
  readonly activeTab: BrowserTabSnapshot | null;
  readonly activeTabUrl: string | null;
  readonly activeScreenshotMeta:
    | {
        readonly url: string;
        readonly capturedAt: string;
        readonly viewport: BrowserViewport;
      }
    | null
    | undefined;
  readonly session: BrowserSessionSnapshot | null;
  readonly hasOrchestration: boolean;
}): BrowserCommentSubmitInput {
  return {
    hasServerThread: input.hasServerThread,
    target: input.target,
    note: input.note,
    activeTabUrl: input.activeTabUrl,
    activeTabId: input.activeTab?.tabId,
    activeScreenshotUrl: input.activeScreenshotMeta?.url,
    activeScreenshotCapturedAt: input.activeScreenshotMeta?.capturedAt,
    sessionViewport: input.session?.viewport,
    captureViewport: input.activeScreenshotMeta?.viewport,
    hasOrchestration: input.hasOrchestration,
  };
}

function createBrowserCommentTurnCommand(input: {
  readonly threadId: ThreadId;
  readonly thread: BrowserCommentThread;
  readonly note: string;
  readonly target: BrowserCommentTarget;
}): BrowserCommentTurnCommand {
  return {
    type: "thread.turn.start",
    commandId: newCommandId(),
    threadId: input.threadId,
    message: {
      messageId: newMessageId(),
      role: "user",
      text: buildBrowserCommentPrompt({
        note: input.note,
        target: input.target,
      }),
      attachments: [],
    },
    modelSelection: input.thread.modelSelection,
    runtimeMode: input.thread.runtimeMode,
    interactionMode: input.thread.interactionMode,
    createdAt: new Date().toISOString(),
  };
}

function showFreshBrowserCommentCaptureToast(): void {
  toastManager.add({
    type: "warning",
    title: "Annotation needs a fresh capture",
    description: "Refresh the browser capture, then select the page area again.",
  });
}

function showBrowserCommentSentToast(): void {
  toastManager.add({
    type: "success",
    title: "Browser comment sent",
    description: "The page comment was added to the thread with browser context.",
  });
}

function browserAutoCaptureRetryDelays(
  activeRenderMode: BrowserRenderMode,
): BrowserAutoCaptureRetryDelays {
  if (activeRenderMode === "interactive") {
    return [
      INTERACTIVE_FRAME_FALLBACK_CAPTURE_MS,
      ...VISIBLE_BROWSER_CAPTURE_RETRY_DELAYS_MS.slice(1),
    ];
  }
  return VISIBLE_BROWSER_CAPTURE_RETRY_DELAYS_MS;
}

function browserAutoCaptureTarget(input: {
  readonly tabId: string | null | undefined;
  readonly url: string | null | undefined;
  readonly updatedAt: string | null | undefined;
}): BrowserAutoCaptureTarget | null {
  if (!input.url) {
    return null;
  }
  if (!input.tabId) {
    return null;
  }
  return {
    tabId: input.tabId,
    url: input.url,
    updatedAt: input.updatedAt ?? "",
  };
}

function shouldAutoCaptureBrowser(input: {
  readonly activeRenderMode: BrowserRenderMode;
  readonly activeFrameIsFresh: boolean;
  readonly activeScreenshotIsFresh: boolean;
  readonly shouldPreferBrowserCapture: boolean;
}): boolean {
  if (input.activeFrameIsFresh || input.activeScreenshotIsFresh) {
    return false;
  }
  return input.activeRenderMode === "interactive" || input.shouldPreferBrowserCapture;
}

function resolveBrowserAutoCapturePlan(input: {
  readonly activeTabId: string | null | undefined;
  readonly activeTabUrl: string | null | undefined;
  readonly activeTabUpdatedAt: string | null | undefined;
  readonly activeRenderMode: BrowserRenderMode | null;
  readonly activeFrameIsFresh: boolean;
  readonly activeScreenshotIsFresh: boolean;
  readonly shouldPreferBrowserCapture: boolean;
  readonly viewportHeight: number | null | undefined;
  readonly viewportWidth: number | null | undefined;
}): {
  readonly captureKey: string;
  readonly tabId: string;
  readonly retryDelays: BrowserAutoCaptureRetryDelays;
} | null {
  const target = browserAutoCaptureTarget({
    tabId: input.activeTabId,
    url: input.activeTabUrl,
    updatedAt: input.activeTabUpdatedAt,
  });
  if (!target) {
    return null;
  }
  if (!input.activeRenderMode) {
    return null;
  }
  if (
    !shouldAutoCaptureBrowser({
      activeRenderMode: input.activeRenderMode,
      activeFrameIsFresh: input.activeFrameIsFresh,
      activeScreenshotIsFresh: input.activeScreenshotIsFresh,
      shouldPreferBrowserCapture: input.shouldPreferBrowserCapture,
    })
  ) {
    return null;
  }

  return {
    tabId: target.tabId,
    captureKey: `${target.tabId}:${target.url}:${target.updatedAt}:${input.viewportWidth}x${input.viewportHeight}`,
    retryDelays: browserAutoCaptureRetryDelays(input.activeRenderMode),
  };
}

function scheduleBrowserAutoCaptureAttempts(input: {
  readonly tabId: string;
  readonly retryDelays: BrowserAutoCaptureRetryDelays;
  readonly requestScreenshotCapture: (
    tabId: string,
    options?: BrowserCommandRunOptions,
  ) => Promise<void>;
}): () => void {
  let cancelled = false;
  let timeout: number | undefined;

  const runCaptureAttempt = (attempt: number) => {
    const delay = input.retryDelays[Math.min(attempt, input.retryDelays.length - 1)] ?? 0;
    timeout = window.setTimeout(() => {
      if (cancelled) {
        return;
      }
      const isFinalAttempt = attempt >= input.retryDelays.length - 1;
      void input
        .requestScreenshotCapture(input.tabId, {
          visiblePending: false,
          reportError: isFinalAttempt,
        })
        .catch(() => undefined)
        .finally(() => {
          if (!cancelled && !isFinalAttempt) {
            runCaptureAttempt(attempt + 1);
          }
        });
    }, delay);
  };

  runCaptureAttempt(0);

  return () => {
    cancelled = true;
    if (timeout !== undefined) {
      window.clearTimeout(timeout);
    }
  };
}

async function dataUrlToFile(input: {
  readonly dataUrl: string;
  readonly name: string;
  readonly mimeType: string;
}): Promise<File> {
  const response = await fetch(input.dataUrl);
  const blob = await response.blob();
  return new File([blob], input.name, { type: input.mimeType });
}

async function createScreenshotComposerAttachment(input: {
  readonly dataUrl: string;
  readonly url: string | null;
}): Promise<ComposerImageAttachment> {
  const metadata = buildBrowserScreenshotAttachmentMetadata({
    dataUrl: input.dataUrl,
    url: input.url,
    nowMs: Date.now(),
  });
  const file = await dataUrlToFile({
    dataUrl: input.dataUrl,
    name: metadata.name,
    mimeType: metadata.mimeType,
  });
  if (file.size === 0) {
    throw new Error("The browser returned an empty screenshot.");
  }
  return {
    type: "image",
    id: randomUUID(),
    name: metadata.name,
    mimeType: metadata.mimeType,
    sizeBytes: file.size,
    previewUrl: input.dataUrl,
    file,
  };
}

async function waitForBrowserScreenshotDataUrl(input: {
  readonly threadRef: ReturnType<typeof scopeThreadRef>;
  readonly tabId: string;
  readonly tabUrl: string;
  readonly requestedAt: string;
  readonly sessionViewport?: { readonly width: number; readonly height: number } | null;
}): Promise<string | undefined> {
  for (let attempt = 0; attempt < 10; attempt += 1) {
    const latestThreadState = selectBrowserThreadState(
      useBrowserStateStore.getState().browserStateByThreadKey,
      input.threadRef,
    );
    const dataUrl = latestThreadState?.lastScreenshotDataUrlByTabId[input.tabId];
    const meta = latestThreadState?.lastScreenshotMetaByTabId[input.tabId];
    if (
      dataUrl &&
      hasUsableBrowserScreenshotDataUrl(dataUrl) &&
      isFreshBrowserCapture({
        tabUrl: input.tabUrl,
        captureUrl: meta?.url,
        capturedAt: meta?.capturedAt,
        requestedAt: input.requestedAt,
        sessionViewport: input.sessionViewport,
        captureViewport: meta?.viewport,
      })
    ) {
      return dataUrl;
    }
    await new Promise((resolve) => window.setTimeout(resolve, 50));
  }
  return undefined;
}

export function useBrowserCommentCapture(options: BrowserCommentCaptureOptions) {
  const {
    activeFrameIsFresh,
    activeRenderMode,
    activeScreenshotIsFresh,
    activeScreenshotMeta,
    activeTab,
    activeTabUrl,
    addComposerDraftImage,
    canAnnotate,
    clearCommentDraft,
    commentDraft,
    commentOrchestration,
    hasServerThread,
    runCommand,
    serverThread,
    session,
    setCommentSelecting,
    setCommentTarget,
    shouldPreferBrowserCapture,
    threadId,
    threadRef,
  } = options;
  const autoCaptureKeyRef = useRef<string | null>(null);

  const handleCanvasTargetChange = useCallback(
    (target: BrowserCommentTarget | null) => {
      setCommentTarget(threadRef, target);
    },
    [setCommentTarget, threadRef],
  );

  const handleSubmitComment = useCallback(async () => {
    const submitAction = resolveBrowserCommentSubmitAction(
      createBrowserCommentSubmitInput({
        hasServerThread,
        target: commentDraft.target,
        note: commentDraft.note,
        activeTab,
        activeTabUrl,
        activeScreenshotMeta,
        session,
        hasOrchestration: Boolean(commentOrchestration),
      }),
    );
    if (submitAction === "blocked") {
      return;
    }
    if (submitAction === "needs-fresh-capture") {
      clearCommentDraft(threadRef);
      showFreshBrowserCommentCaptureToast();
      return;
    }
    if (!serverThread || !commentDraft.target || !commentOrchestration) {
      return;
    }

    await commentOrchestration.dispatchCommand(
      createBrowserCommentTurnCommand({
        thread: serverThread,
        note: commentDraft.note,
        target: commentDraft.target,
        threadId,
      }),
    );

    clearCommentDraft(threadRef);
    showBrowserCommentSentToast();
  }, [
    activeScreenshotMeta,
    activeTab,
    activeTabUrl,
    clearCommentDraft,
    commentDraft.note,
    commentDraft.target,
    commentOrchestration,
    hasServerThread,
    serverThread,
    session,
    threadId,
    threadRef,
  ]);

  const handleToggleAnnotationMode = useCallback(() => {
    const action = resolveBrowserAnnotationToggleAction({
      canAnnotate,
      selecting: commentDraft.selecting,
    });
    if (action === "needs-capture") {
      toastManager.add({
        type: "warning",
        title: "Capture required",
        description: "Open a page and wait for a fresh browser capture before annotating.",
      });
      return;
    }
    if (action === "clear-selection") {
      clearCommentDraft(threadRef);
      return;
    }
    setCommentSelecting(threadRef, true);
  }, [canAnnotate, clearCommentDraft, commentDraft.selecting, setCommentSelecting, threadRef]);

  const requestScreenshotCapture = useCallback(
    async (tabId: string, runOptions?: BrowserCommandRunOptions) => {
      await runCommand({ type: "screenshot", tabId }, runOptions);
    },
    [runCommand],
  );

  const handleCaptureScreenshotAttachment = useCallback(async () => {
    if (!activeTab || !activeTabUrl) {
      toastManager.add({
        type: "warning",
        title: "No page to capture",
        description: "Open a page in Forge Browser before attaching a screenshot.",
      });
      return;
    }
    try {
      const requestedAt = new Date().toISOString();
      await requestScreenshotCapture(activeTab.tabId);
      const dataUrl = await waitForBrowserScreenshotDataUrl({
        threadRef,
        tabId: activeTab.tabId,
        tabUrl: activeTabUrl,
        requestedAt,
        ...(session?.viewport ? { sessionViewport: session.viewport } : {}),
      });
      if (!dataUrl) {
        throw new Error("No fresh screenshot was returned for the current tab.");
      }
      const image = await createScreenshotComposerAttachment({
        dataUrl,
        url: activeTabUrl,
      });
      addComposerDraftImage(threadRef, image);
      toastManager.add({
        type: "success",
        title: "Screenshot attached",
        description: "The current browser capture was added to the composer.",
      });
    } catch (error) {
      toastManager.add({
        type: "error",
        title: "Could not attach screenshot",
        description: error instanceof Error ? error.message : "Failed to capture this page.",
      });
    }
  }, [
    activeTab,
    activeTabUrl,
    addComposerDraftImage,
    requestScreenshotCapture,
    session?.viewport,
    threadRef,
  ]);

  useEffect(() => {
    const plan = resolveBrowserAutoCapturePlan({
      activeTabId: activeTab?.tabId,
      activeTabUrl: activeTab?.url,
      activeTabUpdatedAt: activeTab?.updatedAt,
      activeRenderMode,
      activeFrameIsFresh,
      activeScreenshotIsFresh,
      shouldPreferBrowserCapture,
      viewportHeight: session?.viewport.height,
      viewportWidth: session?.viewport.width,
    });
    if (!plan || autoCaptureKeyRef.current === plan.captureKey) {
      return;
    }
    autoCaptureKeyRef.current = plan.captureKey;

    return scheduleBrowserAutoCaptureAttempts({
      tabId: plan.tabId,
      retryDelays: plan.retryDelays,
      requestScreenshotCapture,
    });
  }, [
    activeFrameIsFresh,
    activeRenderMode,
    activeScreenshotIsFresh,
    activeTab?.tabId,
    activeTab?.updatedAt,
    activeTab?.url,
    requestScreenshotCapture,
    session?.viewport.height,
    session?.viewport.width,
    shouldPreferBrowserCapture,
  ]);

  return {
    handleCanvasTargetChange,
    handleCaptureScreenshotAttachment,
    handleSubmitComment,
    handleToggleAnnotationMode,
    requestScreenshotCapture,
  };
}
